<template>
  <div class="security-info">
    <form v-if="identityRef">
      <div>
        <label class="form-label">Sign in with email</label>
        <div v-for="a in identityRef.addresses" :key="a.address" class="input-value">
          {{ a.address }}
          <div v-if="a.verifiedDate" class="badge text-bg-success">Verified</div>
        </div>
      </div>
      <div v-if="isSelfRef">
        <label class="form-label">Sign in with an auth code</label>
        <AuthCode always-show-timer/>
      </div>
      <section v-if="showGenerateCodeRef" class="generate-code">
        <label class="form-label">Sign in codes</label>
        <div class="input-area">
          <select class="form-select" v-model="codeLifetimeRef">
            <option key="default" :value="0">Default lifetime</option>
            <option v-for="o in codeLifetimeOptions" :key="o" :value="o">{{ o }} days</option>
          </select>
          <button type="button" class="btn btn-warning" @click="generateCode">Get code</button>
        </div>
        <div v-if="codeRef" class="code-value">{{ codeRef.code }} (expires in {{ DateTimeUtil.toRelativeFutureDate(codeRef.expirationDate) }})</div>
      </section>
    </form>
  </div>
</template>

<style lang="scss" scoped>
.security-info {

  form {
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }

  .badge {
    margin-left: 0.25rem;
  }

  section.generate-code {
    margin-bottom: 2rem;

    .input-area {
      display: flex;
      gap: 1rem;
    }

    select {
      max-width: 200px;
    }

    .code-value {
      margin-top: 1rem;
    }
  }
}
</style>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useUserStore } from '@/gp/UserStore'
import { useIdentityApi, SignInCode, UserIdentity } from '@/auth/IdentityApi'
import { LoadMode } from '@/util/AsyncData';
import { UserPrivilege, useTokenManager } from '@/auth/TokenManager';
import { Duration } from 'luxon';
import { DateTimeUtil } from '@/util/LuxonUtil';
import AuthCode from '@/auth/AuthCode.vue';

const props = defineProps({
  userId: String,
})

const identityApi = useIdentityApi()
const userStore = useUserStore()
const tokenManager = useTokenManager()

const userRef = computed(() => userStore.getAsyncUser(props.userId, LoadMode.EnsureLoaded)?.data)
const isSelfRef = computed(() => props.userId == tokenManager.current.userId)
const identityRef = ref<UserIdentity | undefined>(undefined)
const showGenerateCodeRef = computed(() => tokenManager.current.hasPrivilege(UserPrivilege.ImpersonateUser) && userRef.value?.email?.startsWith('apptest@'));
const codeLifetimeRef = ref(0)
const codeLifetimeOptions = [1, 2, 3, 4, 5, 7, 10, 14]
const codeRef = ref<SignInCode | undefined>(undefined)

watch(userRef, async () => {
  if (userRef.value) {
    identityRef.value = await identityApi.getIdentityAsync(userRef.value.subject!)
  }
}, { immediate: true })

async function generateCode() {
  if (userRef.value) {
    const lifetime = codeLifetimeRef.value ? Duration.fromObject({ days: codeLifetimeRef.value }) : undefined
    codeRef.value = await identityApi.generateSignInCodeAsync(userRef.value.subject!, lifetime)
  }
}

</script>
